import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Link2, Loader2, FileText, Home, Image, CheckCircle, AlertCircle, X, Copy, Plus, ExternalLink } from 'lucide-react';

const SocialImport = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // null | 'auto' | 'manual'
  const [url, setUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);

  // Editable fields
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [importAs, setImportAs] = useState('blog');
  const [category, setCategory] = useState('Market Updates');

  const handleExtract = async () => {
    if (!url.trim()) { toast.error('Please enter a Facebook post URL'); return; }
    setExtracting(true);
    setExtractionResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/social-import/facebook`, { url, import_as: importAs }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });

      const { data, success, message } = response.data;
      setExtractionResult({ success, message });

      if (success && (data.text || data.images?.length)) {
        setContent(data.text || '');
        setImages(data.images || []);
        setTitle(data.text?.split('\n')[0]?.substring(0, 100) || 'Imported Post');
        setMode('manual'); // Switch to manual mode with pre-filled data
        toast.success('Content extracted! Review and edit below.');
      } else {
        setMode('manual'); // Open manual mode
        toast.info('Auto-extraction couldn\'t reach this post. Paste the content manually.');
      }
    } catch (error) {
      setExtractionResult({ success: false, message: 'Network error' });
      setMode('manual');
      toast.info('Paste the content manually below.');
    } finally {
      setExtracting(false);
    }
  };

  const startManualMode = () => {
    setMode('manual');
    setExtractionResult(null);
  };

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) { toast.error('Title and content are required'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/social-import/save`, {
        title, content, images, source_url: url, import_as: importAs, category
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(response.data.message);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setUrl(''); setTitle(''); setContent(''); setImages([]);
    setMode(null); setExtractionResult(null); setNewImageUrl('');
  };

  const removeImage = (index) => setImages(images.filter((_, i) => i !== index));

  const addImage = () => {
    if (newImageUrl.trim()) {
      setImages([...images, newImageUrl.trim()]);
      setNewImageUrl('');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      {/* Header */}
      <header className="crystal-glass fixed top-0 left-0 right-0 z-50" data-testid="social-import-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-[#D4AF37] tracking-tight">
                NOK <span className="text-white/40 font-light mx-1">|</span> <span className="text-white font-light text-base md:text-lg">Admin</span>
              </h1>
            </Link>
            <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" className="text-[#94A3B8] hover:text-white hover:bg-white/5 rounded-full" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="pt-32 pb-24 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Page header */}
          <div className="mb-10">
            <p className="overline text-[#D4AF37] mb-3">Admin Tool</p>
            <h2 className="font-heading text-4xl font-semibold text-white tracking-tight mb-3">Social Media Import</h2>
            <p className="text-[#94A3B8] text-sm leading-relaxed">
              Import content from Facebook posts into your site as a blog post or property listing.
            </p>
          </div>

          {/* Mode Selection (if not in a mode yet) */}
          {!mode && (
            <div className="space-y-4" data-testid="mode-selection">
              {/* Auto Extract Card */}
              <div className="bg-[#131A38] rounded-2xl border border-white/5 p-6 md:p-8">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Link2 className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Auto-Extract from URL</h3>
                    <p className="text-[#94A3B8] text-sm">Paste a public Facebook post URL and we'll try to pull the content automatically.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://www.facebook.com/..."
                    className="bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                    data-testid="facebook-url-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleExtract()}
                  />
                  <Button
                    onClick={handleExtract}
                    disabled={extracting}
                    className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-xl px-6 h-12 flex-shrink-0"
                    data-testid="extract-button"
                  >
                    {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Extract'}
                  </Button>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-white/5" />
                <span className="text-[#475569] text-xs uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-white/5" />
              </div>

              {/* Manual Entry Card */}
              <div
                className="bg-[#131A38] rounded-2xl border border-white/5 p-6 md:p-8 cursor-pointer hover:border-[#D4AF37]/20 transition-colors duration-300"
                onClick={startManualMode}
                data-testid="manual-mode-card"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Copy className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Paste Content Manually</h3>
                    <p className="text-[#94A3B8] text-sm">Copy text and images from a Facebook post and paste them here. Works for any post including private ones.</p>
                  </div>
                </div>
              </div>

              {/* How to instructions */}
              <div className="bg-[#0A0E27] rounded-xl border border-white/5 p-5">
                <p className="text-[#94A3B8] text-xs font-medium mb-3 uppercase tracking-wider">How to copy from Facebook</p>
                <ol className="space-y-2 text-[#94A3B8] text-sm">
                  <li className="flex gap-2"><span className="text-[#D4AF37] font-semibold">1.</span> Open the Facebook post in your browser</li>
                  <li className="flex gap-2"><span className="text-[#D4AF37] font-semibold">2.</span> Select all text in the post and copy it</li>
                  <li className="flex gap-2"><span className="text-[#D4AF37] font-semibold">3.</span> Right-click images and copy the image URL</li>
                  <li className="flex gap-2"><span className="text-[#D4AF37] font-semibold">4.</span> Paste everything into the form below</li>
                </ol>
              </div>
            </div>
          )}

          {/* Manual Entry Form */}
          {mode === 'manual' && (
            <div className="space-y-6" data-testid="manual-entry-form">
              {/* Extraction result notice */}
              {extractionResult && (
                <div className={`p-4 rounded-xl flex items-start gap-3 ${
                  extractionResult.success
                    ? 'bg-emerald-500/10 border border-emerald-500/20'
                    : 'bg-orange-500/10 border border-orange-500/20'
                }`}>
                  {extractionResult.success
                    ? <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    : <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  }
                  <div>
                    <p className={`text-sm font-medium ${extractionResult.success ? 'text-emerald-300' : 'text-orange-300'}`}>
                      {extractionResult.success ? 'Content extracted! Review and edit below.' : 'Auto-extraction couldn\'t reach this post.'}
                    </p>
                    {!extractionResult.success && (
                      <p className="text-[#94A3B8] text-xs mt-1">Paste the post content and image URLs manually below.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Source URL */}
              {url && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#475569]">Source:</span>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:text-[#E2C364] flex items-center gap-1 truncate">
                    {url.substring(0, 60)}... <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                </div>
              )}

              {/* Import settings */}
              <div className="bg-[#131A38] rounded-2xl border border-white/5 p-6 md:p-8">
                <h3 className="text-white font-medium mb-5">Import Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">Save As</Label>
                    <Select value={importAs} onValueChange={setImportAs}>
                      <SelectTrigger className="border-white/10 bg-[#0A0E27] text-white focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] rounded-xl h-11" data-testid="import-as-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="blog"><div className="flex items-center gap-2"><FileText className="w-4 h-4" /> Blog Post</div></SelectItem>
                        <SelectItem value="property"><div className="flex items-center gap-2"><Home className="w-4 h-4" /> Property Listing</div></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {importAs === 'blog' && (
                    <div>
                      <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">Category</Label>
                      <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="border-white/10 bg-[#0A0E27] text-white focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] rounded-xl h-11" data-testid="category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['Market Updates', 'Investment Tips', 'Insurance', 'Legal', 'Retirement Planning', 'Tax Information'].map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="bg-[#131A38] rounded-2xl border border-white/5 p-6 md:p-8">
                <h3 className="text-white font-medium mb-5">Content</h3>
                <div className="space-y-5">
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">Title *</Label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter a title for this post"
                      className="bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-12 rounded-xl"
                      data-testid="post-title-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-medium mb-2 block">Content *</Label>
                    <Textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Paste the Facebook post text here..."
                      rows={8}
                      className="bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 rounded-xl resize-y"
                      data-testid="post-content-textarea"
                    />
                  </div>
                </div>
              </div>

              {/* Images */}
              <div className="bg-[#131A38] rounded-2xl border border-white/5 p-6 md:p-8">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-white font-medium">Images ({images.length})</h3>
                </div>

                {/* Add image URL input */}
                <div className="flex gap-2 mb-4">
                  <Input
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="Paste image URL here (right-click image on FB > Copy image address)"
                    className="bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 h-10 rounded-xl text-sm"
                    data-testid="add-image-url-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addImage(); } }}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); addImage(); }}
                    className="flex items-center justify-center gap-2 border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-xl h-10 px-5 flex-shrink-0 transition-colors duration-200 text-sm font-medium"
                    data-testid="add-image-btn"
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>

                {images.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {images.map((img, i) => (
                      <div key={i} className="relative group aspect-video rounded-xl overflow-hidden border border-white/5 bg-[#0A0E27]">
                        <img src={img} alt={`Image ${i + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; }} />
                        <button
                          onClick={() => removeImage(i)}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          data-testid={`remove-image-${i}`}
                        >
                          <X className="w-3.5 h-3.5 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 border border-dashed border-white/10 rounded-xl text-center">
                    <Image className="w-8 h-8 mx-auto text-[#475569] mb-2" />
                    <p className="text-[#475569] text-sm">No images yet. Paste image URLs above.</p>
                    <p className="text-[#475569] text-xs mt-1">Tip: Right-click an image on Facebook and select "Copy image address"</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <Button
                  onClick={handleSave}
                  disabled={saving || !title || !content}
                  className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full px-8 h-12 text-sm"
                  data-testid="save-post-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {saving ? 'Saving...' : `Save as ${importAs === 'blog' ? 'Blog Post' : 'Property Draft'}`}
                </Button>
                <Button
                  onClick={resetForm}
                  variant="outline"
                  className="border-white/10 text-[#94A3B8] hover:bg-white/5 hover:text-white rounded-full px-6 h-12 text-sm"
                  data-testid="reset-form-button"
                >
                  Start Over
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialImport;
